import { useUpdateTableViewCellApi } from '@/api/tableViews';
import { DataTable } from '@/components/table/DataTable';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Text } from '@/components/ui/text';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useDebounce } from '@/hooks/useDebounce';
import { useFooter } from '@/hooks/useFooterContext';
import { useSidebar } from '@/hooks/useSidebarContext';
import {
  useCreateTableViewColumnService,
  useDeleteTableViewColumnService,
  useDeleteTableViewRowsService,
  useExecuteTableViewAnalysisService,
  useExportTableViewService,
  useGenerateTableViewColumnsService,
  useInitializeFromProductCodeService,
  useReorderTableViewColumnsService,
  useTableViewService,
  useTableViewUploadService,
  useUpdateTableViewColumnService,
} from '@/services/tableViews';
import type { TableViewColumnDto, TableViewFilters } from '@ditto/shared';
import {
  getPaginationRowModel,
  getSortedRowModel,
} from '@tanstack/react-table';
import { ChevronDown, Download, Expand, Plus, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import CreateColumnIcon from '../../../assets/add-column.svg?react';
import CreateColumnsIcon from '../../../assets/add-columns.svg?react';
import Grid2x2StarIcon from '../../../assets/grid2x2-star.svg?react';
import QalicoIcon from '../../../assets/qalico.svg?react';
import { TemplatesDialog } from '../components/TemplateDialog';
import { TableViewActionBar } from './components/ActionBar';
import { AddItemsPanel } from './components/AddItemsPanel';
import {
  AddItemsSidebar,
  type AddItemsMode,
} from './components/AddItemsSidebar';
import {
  ColumnModal,
  type ColumnOption,
  type ColumnType,
} from './components/ColumnModal';
import { GenerationColumnModal } from './components/GenerationColumnModal';
import { SourceSidePanel } from './components/SourceSidePanel';
import { TableViewChatPanel } from './components/TableViewChatPanel';
import {
  TableViewFilters as TableViewFiltersComponent,
  type ColumnFilter,
} from './components/TableViewFilters';
import { TableViewHeader } from './components/TableViewHeader';
import { useErrorRowsToast } from './hooks/useErrorRowsToast';
import { useProgressBar } from './hooks/useProgressBar';
import { useTableViewColumns } from './hooks/useTableViewColumns';

export const TableViewPage: FC = () => {
  const { tableView_id } = useParams<{ tableView_id: string }>();
  const location = useLocation();
  const { setFooterInfo } = useFooter();

  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<ColumnFilter[]>([]);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const tableViewFilters: TableViewFilters | undefined = useMemo(() => {
    const optionFilters = filters.filter(
      (f): f is Extract<ColumnFilter, { type: 'options' }> =>
        f.type === 'options'
    );
    const dateFilters = filters.filter(
      (f): f is Extract<ColumnFilter, { type: 'date' }> =>
        f.type === 'date' && !!(f.from || f.to)
    );
    const hasSearchQuery = debouncedSearchQuery.trim().length > 0;

    if (
      optionFilters.length === 0 &&
      dateFilters.length === 0 &&
      !hasSearchQuery
    ) {
      return undefined;
    }

    return {
      ...(optionFilters.length > 0 && {
        columnFilters: JSON.stringify(
          optionFilters.map(f => ({
            columnId: f.columnId,
            optionIds: f.selectedOptionIds,
          }))
        ),
      }),
      ...(dateFilters.length > 0 && {
        dateFilters: JSON.stringify(
          dateFilters.map(f => ({
            columnId: f.columnId,
            from: f.from,
            to: f.to,
          }))
        ),
      }),
      ...(hasSearchQuery && {
        searchQuery: debouncedSearchQuery.trim(),
      }),
    };
  }, [filters, debouncedSearchQuery]);

  const { sidebarWidth } = useSidebar();
  const { tableView, isPending, refetch, isGenerating } = useTableViewService(
    tableView_id ?? '',
    tableViewFilters
  );
  const { isUploading } = useTableViewUploadService();
  const { generateTableViewColumns, isGeneratingColumns } =
    useGenerateTableViewColumnsService();
  const { createTableViewColumn } = useCreateTableViewColumnService();
  const { updateTableViewColumn } = useUpdateTableViewColumnService();
  const { deleteTableViewColumn } = useDeleteTableViewColumnService();
  const { deleteTableViewRows, isPending: isDeletingRow } =
    useDeleteTableViewRowsService();
  const { executeAnalysis } = useExecuteTableViewAnalysisService();
  const { downloadExport, isExporting } = useExportTableViewService();
  const { isInitializing } = useInitializeFromProductCodeService();
  const { reorderColumns } = useReorderTableViewColumnsService();
  const { mutate: updateCell } = useUpdateTableViewCellApi();

  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(
    // Open template dialog if navigated from router with 0 table views
    (location.state as { openTemplateDialog?: boolean } | null)
      ?.openTemplateDialog ?? false
  );
  const [columnGenerationPrompt, setColumnGenerationPrompt] = useState('');
  const [columns, setColumns] = useState<TableViewColumnDto[]>([]);

  const [isChatOpen, setIsChatOpen] = useState(false);
  const [addItemsMode, setAddItemsModeState] = useState<AddItemsMode>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedRowId, setSelectedRowIdState] = useState<string | null>(null);

  // Ensure AddItemsPanel and SourceSidePanel are mutually exclusive
  const setAddItemsMode = useCallback((mode: AddItemsMode) => {
    setAddItemsModeState(mode);
    if (mode !== null) {
      setSelectedRowIdState(null);
    }
  }, []);

  const setSelectedRowId = useCallback((rowId: string | null) => {
    setSelectedRowIdState(rowId);
    if (rowId !== null) {
      setAddItemsModeState(null);
    }
  }, []);

  // Open add items panel based on URL parameter (from template navigation)
  useEffect(() => {
    const panelParam = searchParams.get('panel');
    if (panelParam === 'database' || panelParam === 'files') {
      setAddItemsMode(panelParam);
      // Remove the parameter from URL to avoid re-opening on refresh
      searchParams.delete('panel');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    setColumns(tableView?.columns ?? []);
    setColumnGenerationPrompt(tableView?.columnGenerationPrompt ?? '');
  }, [tableView]);

  // Show toast when there are error rows
  useErrorRowsToast({
    tableViewId: tableView_id,
    rows: tableView?.rows,
    columns,
    isGenerating,
  });

  const handleGenerateColumns = () => {
    if (!tableView_id || !columnGenerationPrompt.trim()) {
      return;
    }
    generateTableViewColumns(
      {
        tableViewId: tableView_id,
        data: {
          prompt: columnGenerationPrompt,
        },
      },
      {
        onSuccess: async data => {
          setIsGenerateModalOpen(false);

          // Refetch to get the updated table view
          const result = await refetch();
          const newColumnIds = data?.columnIds ?? [];

          // Execute analysis only on the newly generated columns
          if (
            newColumnIds.length > 0 &&
            result.data?.rows &&
            result.data.rows.length > 0
          ) {
            executeAnalysis({
              tableViewId: tableView_id,
              data: {
                columnIds: newColumnIds,
                rowIds: result.data.rows.map(row => row.id),
              },
            });
          }
        },
        onError: () => {
          toast.error('Failed to generate columns');
        },
      }
    );
  };

  const handleCreateColumn = useCallback(
    (
      name: string,
      prompt: string,
      type: ColumnType,
      options?: ColumnOption[]
    ) => {
      const order =
        columns.length > 0
          ? Math.max(...columns.map(column => column.order)) + 1
          : 0;
      if (!tableView_id) {
        return;
      }

      createTableViewColumn(
        {
          name,
          prompt,
          type,
          order,
          tableViewId: tableView_id,
          options: options?.map(opt => ({ value: opt.value })),
        },
        {
          onSuccess: data => {
            const newColumn: TableViewColumnDto = {
              id: data.id,
              name: data.name,
              prompt: data.prompt,
              type: data.type,
              order: data.order,
              options: data.options ?? null,
              isGenerating: false,
            };

            setColumns(prev => [...prev, newColumn]);

            // Execute analysis for the new column if there are rows
            if (tableView?.rows && tableView.rows.length > 0) {
              executeAnalysis({
                tableViewId: tableView_id,
                data: {
                  columnIds: [data.id],
                  rowIds: tableView.rows.map(row => row.id),
                },
              });
            }
          },
          onError: () => {
            toast.error('Failed to create the column');
          },
        }
      );
    },
    [columns, tableView_id, createTableViewColumn, executeAnalysis]
  );

  const handleUpdateColumn = useCallback(
    (
      id: string,
      name: string,
      prompt: string,
      type: ColumnType,
      options?: ColumnOption[],
      nameOnly?: boolean
    ) => {
      if (!tableView_id) {
        return;
      }

      const existingColumn = columns.find(col => col.id === id);
      if (!existingColumn) {
        return;
      }

      updateTableViewColumn(
        {
          columnId: id,
          tableViewId: tableView_id,
          data: {
            name,
            prompt,
            type,
            order: existingColumn.order,
            options: options?.map(opt => ({
              id: opt.id ?? '',
              value: opt.value,
            })),
          },
        },
        {
          onSuccess: async data => {
            // Refetch to get updated cell statuses
            await refetch();

            setColumns(prev =>
              prev.map(col =>
                col.id === id
                  ? {
                      ...col,
                      name,
                      prompt,
                      type,
                      options: data.options ?? null,
                    }
                  : col
              )
            );

            // Only execute analysis (regenerate cells) if not a name-only change
            if (!nameOnly && tableView?.rows && tableView.rows.length > 0) {
              executeAnalysis({
                tableViewId: tableView_id,
                data: {
                  columnIds: [id],
                  rowIds: tableView.rows.map(row => row.id),
                },
              });
            }
          },
          onError: () => {
            toast.error('Failed to update the column');
          },
        }
      );
    },
    [columns, tableView_id, updateTableViewColumn, executeAnalysis, refetch]
  );

  const handleDeleteColumn = useCallback(
    (columnId: string) => {
      if (!tableView_id) {
        return;
      }

      deleteTableViewColumn(
        {
          columnId,
          tableViewId: tableView_id,
        },
        {
          onSuccess: () => {
            setColumns(prev => prev.filter(col => col.id !== columnId));
          },
          onError: () => {
            toast.error('Failed to delete the column');
          },
        }
      );
    },
    [tableView_id, deleteTableViewColumn]
  );

  const handleDownload = useCallback(async () => {
    if (!tableView_id) return;
    try {
      await downloadExport(tableView_id);
      toast.success('Export started');
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to export table view'
      );
    }
  }, [tableView_id, downloadExport]);

  const handleColumnOrderChange = useCallback(
    (columnOrder: string[]) => {
      if (!tableView_id) return;
      // Filter to only actual table view column IDs (UUIDs from the columns state)
      const columnIdSet = new Set(columns.map(c => c.id));
      const reorderedColumnIds = columnOrder.filter(id => columnIdSet.has(id));
      if (reorderedColumnIds.length === 0) return;

      // Update local columns state to reflect the new order
      setColumns(prev => {
        const newColumns = [...prev];
        reorderedColumnIds.forEach((id, index) => {
          const col = newColumns.find(c => c.id === id);
          if (col) col.order = index;
        });
        return newColumns.sort((a, b) => a.order - b.order);
      });

      reorderColumns({
        tableViewId: tableView_id,
        data: { columnIds: reorderedColumnIds },
      });
    },
    [tableView_id, columns, reorderColumns]
  );

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const selectedRowsCount = Object.values(rowSelection).filter(Boolean).length;
  const displayActionBar = selectedRowsCount > 0;

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });

  // Fake progress for upload/initialization
  const isLoadingFiles = isUploading || isInitializing;
  const { progress: uploadProgress, showProgress: showUploadProgress } =
    useProgressBar({ isLoading: isLoadingFiles });

  const clearRowSelection = () => {
    setRowSelection({});
  };

  const hasNoRows = useMemo(
    () => tableView?.rows?.length === 0,
    [tableView?.rows?.length]
  );

  const handleDeleteRow = useCallback(
    (rowId: string) => {
      if (!tableView_id) return;
      deleteTableViewRows(
        { tableViewId: tableView_id, rowIds: rowId },
        {
          onSuccess: () => {
            toast.success('Row deleted successfully');
          },
          onError: () => {
            toast.error('Failed to delete row');
          },
        }
      );
    },
    [tableView_id, deleteTableViewRows]
  );

  const handleMoveColumn = useCallback(
    (columnId: string, direction: 'left' | 'right') => {
      const currentIndex = columns.findIndex(c => c.id === columnId);
      if (currentIndex === -1) return;
      const targetIndex =
        direction === 'left' ? currentIndex - 1 : currentIndex + 1;
      if (targetIndex < 0 || targetIndex >= columns.length) return;

      const newOrder = columns.map(c => c.id);
      [newOrder[currentIndex], newOrder[targetIndex]] = [
        newOrder[targetIndex],
        newOrder[currentIndex],
      ];
      handleColumnOrderChange(newOrder);
    },
    [columns, handleColumnOrderChange]
  );

  const tableColumns = useTableViewColumns({
    columns,
    onUpdateColumn: handleUpdateColumn,
    onDeleteColumn: handleDeleteColumn,
    onDeleteRow: handleDeleteRow,
    isDeletingRow,
    onAddColumn: handleCreateColumn,
    onMoveColumn: handleMoveColumn,
    setSelectedRowId,
    disabledAddColumn: useMemo(
      () => isUploading || isInitializing,
      [isUploading, isInitializing]
    ),
    hasNoRows,
    hasDatabaseRows: useMemo(
      () => (tableView?.rows ?? []).some(r => !!r.source),
      [tableView?.rows]
    ),
  });

  // Table meta for cell editing
  const tableMeta = useMemo(
    () => ({
      updateCell: (
        rowId: string,
        columnId: string,
        value: unknown,
        columnType: string
      ) => {
        // Map columnType to the correct patch field
        const patch: Record<string, unknown> = {};

        if (columnType === 'text') {
          patch.textValue = value;
        } else if (columnType === 'number') {
          patch.numberValue = value;
        } else if (columnType === 'date') {
          patch.dateValue = value;
        } else if (columnType === 'verbatim') {
          patch.verbatimValue = value;
        }

        updateCell({
          tableViewId: tableView_id ?? '',
          data: { rowId, columnId, patch },
        });
      },
    }),
    [tableView_id, updateCell]
  );

  const filteredRows = tableView?.rows ?? [];

  // Update footer with row counts
  useEffect(() => {
    setFooterInfo({
      totalRows: filteredRows.length,
      selectedRows: selectedRowsCount,
    });

    // Clear footer info when unmounting
    return () => {
      setFooterInfo(null);
    };
  }, [filteredRows.length, selectedRowsCount, setFooterInfo]);

  const lastRow = {
    id: 'last-row',
    order: filteredRows.length + 1,
    fileId: null,
    fileName: null,
    label: null,
    source: null,
    text: null,
    cells: [],
    button: {
      label: 'Add item',
      disabled: isUploading || isInitializing,
      onClick: () => {
        setAddItemsMode('files');
      },
    },
  };

  const blankRows = Array.from({ length: 10 }, (_, index) => ({
    id: `blank-${index}`,
    order: index + 1,
    fileId: null,
    fileName: null,
    label: null,
    source: null,
    text: null,
    cells: [],
  }));

  const rows: typeof filteredRows = [...filteredRows, lastRow, ...blankRows];

  const disabledExport = isExporting || columns.length === 0 || hasNoRows;

  const progressValue = tableView?.stats
    ? ((tableView.stats.totalRows -
        tableView.stats.generationInQueue -
        tableView.stats.generationProgress) /
        tableView.stats.totalRows) *
      100
    : 0;
  const selectedRows = rows.filter(
    row =>
      rowSelection[row.id] &&
      row.id !== 'last-row' &&
      !row.id.startsWith('blank-')
  );

  if (isPending) {
    return (
      <FlexCol w="full" h="full">
        <TableViewHeader title="Loading..." />
        <FlexCol p="6" w="full" gap="6" className="max-w-[1600px]">
          <Text>Loading...</Text>
        </FlexCol>
      </FlexCol>
    );
  }

  if (!tableView) {
    return (
      <FlexCol w="full" h="full">
        <TableViewHeader title="Table view" />
        <FlexCol p="6" w="full" gap="6" className="max-w-[1600px]">
          <Text>Table view not found</Text>
        </FlexCol>
      </FlexCol>
    );
  }

  return (
    <FlexRow className="h-full w-full overflow-hidden relative">
      {isChatOpen && (
        <TableViewChatPanel
          key={tableView_id}
          tableViewId={tableView_id ?? ''}
          onClose={() => setIsChatOpen(false)}
        />
      )}
      <FlexCol className="flex-1 h-full min-w-0 overflow-hidden">
        <TableViewHeader title={tableView.name} />

        <FlexRow className="flex-1 min-h-0 overflow-hidden relative">
          <FlexCol
            p="6"
            w="full"
            gap="6"
            className="flex-1 overflow-hidden pb-0"
          >
            <FlexCol gap="4">
              <FlexRow gap="2" justifyContent="between" alignItems="center">
                <FlexRow gap="2" alignItems="center">
                  <div className="relative w-[320px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
                    <Input
                      placeholder="Search documents"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </FlexRow>

                <FlexRow gap="2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <Button
                          variant="ghost"
                          onClick={handleDownload}
                          disabled={disabledExport}
                        >
                          <Download className="w-4 h-4" />
                          {isExporting ? 'Exporting...' : 'Download'}
                        </Button>
                      </div>
                    </TooltipTrigger>
                    {disabledExport && (
                      <TooltipContent>
                        Empty tables can not be downloaded
                      </TooltipContent>
                    )}
                  </Tooltip>

                  <Button
                    variant="outline"
                    onClick={() => setIsTemplateDialogOpen(true)}
                  >
                    <Grid2x2StarIcon className="w-4 h-4" />
                    Templates
                  </Button>

                  <Button
                    variant="outline"
                    onClick={() => setAddItemsMode('files')}
                    disabled={isUploading}
                  >
                    <Plus className="w-4 h-4" />
                    {isUploading ? 'Uploading...' : 'Add items'}
                  </Button>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="dark">
                        Add columns
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <ColumnModal
                        mode="create"
                        columns={columns}
                        onCreate={handleCreateColumn}
                      >
                        <DropdownMenuItem onSelect={e => e.preventDefault()}>
                          <CreateColumnIcon className="w-4 h-4" />
                          Add a column
                        </DropdownMenuItem>
                      </ColumnModal>
                      <DropdownMenuItem
                        onSelect={() => setIsGenerateModalOpen(true)}
                      >
                        <CreateColumnsIcon className="w-4 h-4" />
                        Bulk create columns
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </FlexRow>
              </FlexRow>

              <GenerationColumnModal
                open={isGenerateModalOpen}
                onOpenChange={setIsGenerateModalOpen}
                columnGenerationPrompt={columnGenerationPrompt}
                setColumnGenerationPrompt={setColumnGenerationPrompt}
                isGeneratingColumns={isGeneratingColumns}
                handleGenerateColumns={handleGenerateColumns}
              />

              <TableViewFiltersComponent
                columns={columns}
                filters={filters}
                onFiltersChange={setFilters}
              />
            </FlexCol>

            <FlexCol gap="2" className="flex-1 min-h-0">
              {isGenerating && (
                <FlexCol>
                  <Progress
                    value={progressValue}
                    className="bg-tan-400 h-1"
                    color="yellow"
                  />
                </FlexCol>
              )}
              {showUploadProgress && (
                <FlexCol>
                  <Progress
                    value={uploadProgress}
                    className="bg-tan-400 h-1"
                    color="yellow"
                  />
                </FlexCol>
              )}
              <DataTable
                columns={tableColumns}
                data={rows}
                isLoading={isPending}
                enableColumnResizing
                columnResizeMode="onChange"
                enableColumnReordering
                onColumnOrderChange={handleColumnOrderChange}
                stickyScrollbar
                scrollX
                nonDraggableColumnIds={[
                  'add-column',
                  'blank-0',
                  'blank-1',
                  'blank-2',
                  'blank-3',
                  'blank-4',
                  'blank-5',
                ]}
                grid="bordered"
                options={{
                  meta: tableMeta,
                  getSortedRowModel: getSortedRowModel(),
                  getPaginationRowModel: getPaginationRowModel(),
                  enableMultiSort: false,
                  initialState: {
                    columnPinning: {
                      left: ['counter', 'select', 'rowInput'],
                      right: [],
                    },
                  },
                  getRowId: row => row.id,
                  onPaginationChange: setPagination,
                  onRowSelectionChange: setRowSelection,
                  autoResetPageIndex: false,
                  enableRowSelection(row) {
                    return (
                      row.id !== 'last-row' && !row.id.startsWith('blank-')
                    );
                  },
                  state: {
                    pagination,
                    rowSelection,
                  },
                }}
              />
            </FlexCol>
            <TableViewActionBar
              open={displayActionBar}
              clearSelection={clearRowSelection}
              columns={columns}
              rows={selectedRows}
              tableViewId={tableView_id ?? ''}
              selectedRowsCount={selectedRowsCount}
            />

            <TemplatesDialog
              open={isTemplateDialogOpen}
              onOpenChange={setIsTemplateDialogOpen}
              isInitializing={isInitializing}
              currentTableViewId={tableView_id ?? ''}
            />
          </FlexCol>
          <AddItemsPanel
            mode={addItemsMode}
            onClose={() => setAddItemsMode(null)}
            tableViewId={tableView_id ?? ''}
          />
          <AddItemsSidebar
            activeMode={addItemsMode}
            onModeChange={setAddItemsMode}
          />
        </FlexRow>
      </FlexCol>

      {/* Source Side Panel - Overlay */}
      {selectedRowId && (
        <>
          {/* Backdrop to close panel on outside click */}
          <div
            className="absolute inset-0 z-10"
            onClick={() => setSelectedRowId(null)}
          />
          <div className="absolute top-4 right-4 bottom-2 z-20">
            <SourceSidePanel
              row={rows.find(r => r.id === selectedRowId) ?? null}
              columns={columns}
              onClose={() => setSelectedRowId(null)}
            />
          </div>
        </>
      )}

      {!isChatOpen && (
        <div
          className="fixed bottom-6 cursor-pointer bg-white border border-neutral-200 rounded-lg shadow-lg z-10 transition-all duration-300 w-[406px]"
          style={{ left: sidebarWidth + 24 }}
          onClick={() => setIsChatOpen(true)}
        >
          <FlexRow
            gap="2.5"
            px="4"
            py="2.5"
            alignItems="center"
            justifyContent="between"
          >
            <FlexRow gap="2.5" alignItems="center">
              <QalicoIcon className="w-6 h-6" />
              <FlexCol gap="0.5">
                <Text variant="body-sm" className="font-medium text-black">
                  Qalico table chat
                </Text>
                <Text variant="body-xs" className="text-black">
                  Ask to analyze and edit your table data
                </Text>
              </FlexCol>
            </FlexRow>
            <Expand className="w-4 h-4" />
          </FlexRow>
        </div>
      )}
    </FlexRow>
  );
};
